import { Injectable, NgZone } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import firebase from 'firebase/compat/app';
import { Logout } from 'src/app/reducers';

import { error } from './errors';
import { SignUpService } from './sign-up/sign-up.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	error: string[] = [];

	constructor(
		private auth: AngularFireAuth,
		private signUpService: SignUpService,
		private router: Router,
		public ngZone: NgZone,
		private store: Store,
	) {}

	removeToastMsg(str) {
		this.error = this.error.filter(err => err !== str);
	}

	addToastMsg(str, timeout = 3) {
		this.error.push(str);

		setTimeout(() => {
			this.removeToastMsg(str);
		}, timeout * 1000);
	}

	public signIn(email: string, password: string) {
		return this.auth
			.signInWithEmailAndPassword(email, password)
			.then(() => {
				this.addToastMsg('Redirecting to home page!', 1);
				this.ngZone.run(() => {
					this.router.navigate(['app/learning']);
				});
			})
			.catch(e => this.showError(e));
	}

	public googleSignIn() {
		this.auth
			.signInWithPopup(new firebase.auth.GoogleAuthProvider())
			.then(() => {
				this.ngZone.run(() => {
					this.router.navigate(['app/learning']);
				});
			})
			.catch(e => this.showError(e));
	}

	public signUp(name, email, password, subscribedToNewsletter) {
		return this.signUpService
			.init(name, email, password, subscribedToNewsletter)
			.then(() => {
				this.ngZone.run(() => {
					this.router.navigate(['app/learning']);
				});
			})
			.catch(e => this.showError(e));
	}

	public googleSignUp(subscribedToNewsletter) {
		return this.signUpService
			.googleInit(subscribedToNewsletter)
			.then(() => {
				this.ngZone.run(() => {
					this.router.navigate(['app/learning']);
				});
			})
			.catch(e => this.showError(e));
	}

	public signOut() {
		return this.auth
			.signOut()
			.then(() => {
				this.addToastMsg('Redirecting to login page!', 1);

				this.ngZone.run(() => {
					this.router.navigate(['/auth/login'], { replaceUrl: true });
					// eslint-disable-next-line @ngrx/prefer-action-creator-in-dispatch
					this.store.dispatch(new Logout());
				});
			})
			.catch(e => this.showError(e));
	}

	public redirectAuth(
		url: 'forgot-password' | 'forgot-email' | 'sign-up' | 'login',
	) {
		this.router.navigateByUrl(`/auth/${url}`, { skipLocationChange: true });
	}

	public resetPassword(email) {
		return this.auth
			.sendPasswordResetEmail(email)
			.then(() => {
				this.addToastMsg(
					'We have sent a link to ' + email + ' to reset your password.',
				);
				this.ngZone.run(() => {
					this.router.navigate(['/auth/login']);
				});
			})
			.catch(e => this.showError(e));
	}

	private showError(e: any) {
		const index = error.findIndex(x => x.code === e.code);

		if (index !== -1) {
			const err = error[index];
			this.addToastMsg(err.message);
		} else {
			this.addToastMsg('Oops, something went wrong!');
		}

		throw new Error('error');
	}
}
